import type {
  ComboboxItem,
  NumberInputProps,
  PasswordInputProps,
  SelectProps,
  TextareaProps,
  TextInputProps
} from '@mantine/core';
import type { DatePickerInputProps } from '@mantine/dates';
import type { ReactNode } from 'react';
import {
  Box,
  CloseButton,
  Combobox,
  Drawer,
  getParsedComboboxData,
  Group,
  InputBase,
  isOptionsGroup,
  NumberInput,
  PasswordInput,
  Popover,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
  UnstyledButton,
  useCombobox
} from '@mantine/core';
import { Calendar, DatePickerInput } from '@mantine/dates';
import { useMediaQuery } from '@mantine/hooks';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  IconFigmaCalendar,
  IconFigmaCheck,
  IconFigmaChevronDown,
  IconFigmaSearch
} from '@/assets/icons';
import { currentLocale, dateToIsoWeek, isoWeekToDate } from '@/utils/format';

// Mantine Calendar radi s datumima kao 'YYYY-MM-DD' stringovima
// (DateStringValue) — ove dvije funkcije su čisto konverzija u/iz Date, bez
// vremenske zone (lokalno vrijeme, isto kao isoWeekToDate/dateToIsoWeek).
const parseDateStringValue = (value: string): Date => {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day);
};

const toDateStringValue = (date: Date): string => {
  const year = String(date.getFullYear()).padStart(4, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const defaultRenderOption: SelectProps['renderOption'] = ({ option, checked }) => (
  <Group gap={8} wrap="nowrap" style={{ minWidth: 0, width: '100%' }}>
    {checked && (
      <Box style={{ flexShrink: 0, display: 'flex', color: 'inherit' }}>
        <IconFigmaCheck size={12} />
      </Box>
    )}
    <Text
      component="span"
      fz="inherit"
      style={{ flex: 1, minWidth: 0, color: 'inherit' }}
      truncate="end"
    >
      {option.label}
    </Text>
  </Group>
);

interface FieldVisualState {
  focused: boolean;
  disabled?: boolean;
  hasError: boolean;
  hasValue: boolean;
}

const getFieldColors = ({ focused, disabled, hasError, hasValue }: FieldVisualState) => {
  if (disabled) {
    return {
      backgroundColor: 'var(--color-bg-surface-3)',
      border: '1px solid var(--color-border-soft)',
      color: 'var(--color-text-muted)'
    };
  }
  if (hasError) {
    return {
      backgroundColor: 'var(--color-bg-surface)',
      border: '1.5px solid var(--color-status-danger)',
      color: 'var(--color-text-primary)'
    };
  }
  if (focused) {
    return {
      backgroundColor: 'var(--color-bg-surface)',
      border: '2px solid var(--color-accent-secondary)',
      color: hasValue ? 'var(--color-text-primary)' : 'var(--color-text-muted)'
    };
  }
  return {
    backgroundColor: 'var(--color-bg-surface-2)',
    border: '1px solid var(--color-border-default)',
    color: hasValue ? 'var(--color-text-primary)' : 'var(--color-text-muted)'
  };
};

const FieldLabel = ({
  label,
  required,
  hasError
}: {
  label: ReactNode;
  required?: boolean;
  hasError: boolean;
}) => (
  <Text
    fz={11}
    fw={600}
    tt="uppercase"
    style={{
      letterSpacing: 0.4,
      color: hasError ? 'var(--color-status-danger)' : 'var(--color-text-muted)'
    }}
  >
    {label}
    {required ? ' *' : ''}
  </Text>
);

const FieldHelper = ({ children, hasError }: { children: ReactNode; hasError: boolean }) => (
  <Text
    fz={11}
    style={{ color: hasError ? 'var(--color-status-danger)' : 'var(--color-text-muted)' }}
  >
    {children}
  </Text>
);

interface MobileSelectSheetProps {
  opened: boolean;
  onClose: () => void;
  title?: ReactNode;
  data: SelectProps['data'];
  value?: string | null;
  onSelect: (item: ComboboxItem) => void;
  searchable?: boolean;
  onCreate?: (query: string) => void;
  createLabel?: (query: string) => ReactNode;
  emptyLabel?: ReactNode;
}

export const MobileSelectSheet = ({
  opened,
  onClose,
  title,
  data,
  value,
  onSelect,
  searchable,
  onCreate,
  createLabel,
  emptyLabel
}: MobileSelectSheetProps) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (opened) setQuery('');
  }, [opened]);

  const trimmedQuery = query.trim();
  const normalizedQuery = trimmedQuery.toLowerCase();
  const matches = (item: ComboboxItem) => item.label.toLowerCase().includes(normalizedQuery);
  const entries = getParsedComboboxData(data)
    .map((entry) =>
      isOptionsGroup(entry) ? { ...entry, items: entry.items.filter(matches) } : entry
    )
    .filter((entry) => (isOptionsGroup(entry) ? entry.items.length > 0 : matches(entry)));
  const allItems = getParsedComboboxData(data).flatMap((entry) =>
    isOptionsGroup(entry) ? entry.items : [entry]
  );
  const showCreate =
    !!onCreate &&
    trimmedQuery.length > 0 &&
    !allItems.some((item) => item.label.toLowerCase() === normalizedQuery);

  const itemStyle = (checked: boolean, disabled?: boolean) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    width: '100%',
    borderRadius: 8,
    opacity: disabled ? 0.5 : 1,
    backgroundColor: checked ? 'var(--color-bg-surface-2)' : undefined,
    color: 'var(--color-text-primary)'
  });

  const renderItem = (item: ComboboxItem) => {
    const checked = item.value === value;
    return (
      <UnstyledButton
        key={item.value}
        disabled={item.disabled}
        onClick={() => onSelect(item)}
        px={16}
        mih={48}
        style={itemStyle(checked, item.disabled)}
      >
        <Text component="span" fz={15} fw={checked ? 600 : 400} style={{ flex: 1, minWidth: 0 }}>
          {item.label}
        </Text>
        {checked && (
          <Box style={{ display: 'flex', flexShrink: 0 }}>
            <IconFigmaCheck size={16} />
          </Box>
        )}
      </UnstyledButton>
    );
  };

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="bottom"
      title={title}
      styles={{
        content: {
          backgroundColor: 'var(--color-bg-surface)',
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          maxHeight: '75dvh',
          flex: '0 0 auto',
          height: 'auto',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        },
        header: {
          backgroundColor: 'var(--color-bg-surface)',
          paddingBottom: 8,
          flexShrink: 0
        },
        title: {
          fontSize: 15,
          fontWeight: 600,
          color: 'var(--color-text-primary)'
        },
        body: {
          padding: '0 8px calc(16px + env(safe-area-inset-bottom))',
          flex: '1 1 auto',
          minHeight: 0,
          overflowY: 'auto',
          overscrollBehavior: 'contain'
        }
      }}
    >
      {searchable && (
        <Box
          px={8}
          pb={8}
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 1,
            backgroundColor: 'var(--color-bg-surface)'
          }}
        >
          <FormTextInput
            value={query}
            onChange={(event) => setQuery(event.currentTarget.value)}
            placeholder={t('common.search')}
            aria-label={t('common.search')}
            leftSection={<IconFigmaSearch size={16} color="var(--color-text-secondary)" />}
            inputHeight={44}
            inputBackground="var(--color-bg-surface)"
          />
        </Box>
      )}
      <Stack gap={2}>
        {entries.map((entry) =>
          isOptionsGroup(entry) ? (
            <Stack key={entry.group} gap={2}>
              <Text
                fz={11}
                fw={600}
                tt="uppercase"
                px={16}
                pt={12}
                pb={4}
                style={{ color: 'var(--color-text-muted)' }}
              >
                {entry.group}
              </Text>
              {entry.items.map(renderItem)}
            </Stack>
          ) : (
            renderItem(entry)
          )
        )}
        {showCreate && (
          <UnstyledButton
            onClick={() => onCreate?.(trimmedQuery)}
            px={16}
            mih={48}
            style={itemStyle(false)}
          >
            <Text component="span" fz={15} fw={600} style={{ flex: 1, minWidth: 0 }}>
              {createLabel ? createLabel(trimmedQuery) : `+ ${trimmedQuery}`}
            </Text>
          </UnstyledButton>
        )}
        {entries.length === 0 && !showCreate && (
          <Text fz={14} ta="center" py={24} style={{ color: 'var(--color-text-muted)' }}>
            {emptyLabel ?? t('common.noResults')}
          </Text>
        )}
      </Stack>
    </Drawer>
  );
};

interface FormSelectProps extends Omit<SelectProps, 'styles' | 'rightSection' | 'label'> {
  label?: ReactNode;
  required?: boolean;
  helperText?: ReactNode;
  error?: ReactNode;
  inputHeight?: number;
  inputBackground?: string;
  chevron?: ReactNode;
}

const LEFT_ICON_SECTION_WIDTH = 36;
const CHEVRON_SECTION_WIDTH = 36;

export const FormSelect = ({
  label,
  required,
  helperText,
  error,
  disabled,
  value,
  onFocus,
  onBlur,
  inputHeight = 42,
  inputBackground,
  chevron,
  ...selectProps
}: FormSelectProps) => {
  const [focused, setFocused] = useState(false);
  const [dropdownOpened, setDropdownOpened] = useState(false);
  const [sheetOpened, setSheetOpened] = useState(false);
  const isMobile = useMediaQuery('(max-width: 48em)');
  const usesSheet = !!isMobile;
  const hasError = !!error;
  const hasValue = value !== null && value !== undefined && value !== '';
  const colors = getFieldColors({ focused, disabled, hasError, hasValue });
  // Kad je polje clearable i ima vrijednost, NE dajemo vlastiti rightSection —
  // Mantine tad sam iscrta svoj "x" clear gumb (i sam poziva onChange(null)).
  // Fiksni chevron ovdje bi ga trajno prekrio, pa bi clearable bio prisutan
  // samo programatski, bez ikakvog vidljivog načina da ga korisnik stvarno
  // očisti.
  const showsOwnClearButton = !!selectProps.clearable && hasValue && !disabled;
  const hasLeftSection = !!selectProps.leftSection;
  const selectedLabel = getParsedComboboxData(selectProps.data)
    .flatMap((entry) => (isOptionsGroup(entry) ? entry.items : [entry]))
    .find((item) => item.value === value)?.label;

  return (
    <Stack gap={6} style={{ opacity: disabled ? 0.6 : 1 }}>
      {label && <FieldLabel label={label} required={required} hasError={hasError} />}
      <Select
        value={value}
        title={selectedLabel}
        onMouseDown={usesSheet ? (event) => event.preventDefault() : undefined}
        disabled={disabled}
        onFocus={(event) => {
          setFocused(true);
          onFocus?.(event);
        }}
        onBlur={(event) => {
          setFocused(false);
          onBlur?.(event);
        }}
        dropdownOpened={usesSheet ? false : undefined}
        onDropdownOpen={() => (usesSheet ? setSheetOpened(true) : setDropdownOpened(true))}
        onDropdownClose={() => setDropdownOpened(false)}
        rightSection={
          showsOwnClearButton ? undefined : (
            <div
              style={{
                display: 'flex',
                transform: dropdownOpened || sheetOpened ? 'rotate(180deg)' : undefined,
                transition: 'transform 150ms ease'
              }}
            >
              {chevron ?? <IconFigmaChevronDown size={14} color={colors.color} />}
            </div>
          )
        }
        rightSectionWidth={chevron ? CHEVRON_SECTION_WIDTH : undefined}
        rightSectionProps={
          chevron ? { style: { justifyContent: 'flex-end', paddingRight: 13 } } : undefined
        }
        leftSectionWidth={hasLeftSection ? LEFT_ICON_SECTION_WIDTH : undefined}
        leftSectionProps={
          hasLeftSection
            ? { style: { justifyContent: 'flex-start', paddingLeft: 13, color: colors.color } }
            : undefined
        }
        styles={{
          input: {
            height: inputHeight,
            paddingLeft: hasLeftSection ? LEFT_ICON_SECTION_WIDTH : 13,
            paddingRight: chevron ? CHEVRON_SECTION_WIDTH : 34,
            borderRadius: 6,
            fontSize: 14.5,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            ...colors,
            ...(inputBackground && !disabled && { backgroundColor: inputBackground })
          },
          dropdown: {
            backgroundColor: 'var(--color-bg-surface)',
            borderColor: 'var(--color-border-default)'
          },
          option: {
            overflow: 'hidden'
          }
        }}
        renderOption={defaultRenderOption}
        {...selectProps}
        searchable={usesSheet ? false : selectProps.searchable}
      />
      {usesSheet && (
        <MobileSelectSheet
          opened={sheetOpened}
          onClose={() => setSheetOpened(false)}
          title={label ?? selectProps['aria-label']}
          data={selectProps.data}
          value={value}
          searchable={selectProps.searchable}
          onSelect={(item) => {
            const deselect = selectProps.allowDeselect !== false && item.value === value;
            selectProps.onChange?.(deselect ? null : item.value, item);
            setSheetOpened(false);
          }}
        />
      )}
      {(helperText || error) && (
        <FieldHelper hasError={hasError}>{error ?? helperText}</FieldHelper>
      )}
    </Stack>
  );
};

interface FormTextInputProps extends Omit<TextInputProps, 'styles' | 'label'> {
  label?: ReactNode;
  required?: boolean;
  helperText?: ReactNode;
  error?: ReactNode;
  inputHeight?: number;
  inputBackground?: string;
}

export const FormTextInput = ({
  label,
  required,
  helperText,
  error,
  disabled,
  value,
  onFocus,
  onBlur,
  inputHeight = 42,
  inputBackground,
  leftSection,
  rightSection,
  ...inputProps
}: FormTextInputProps) => {
  const [focused, setFocused] = useState(false);
  const hasError = !!error;
  const hasValue = value !== null && value !== undefined && value !== '';
  const colors = getFieldColors({ focused, disabled, hasError, hasValue });

  return (
    <Stack gap={6} style={{ opacity: disabled ? 0.6 : 1 }}>
      {label && <FieldLabel label={label} required={required} hasError={hasError} />}
      <TextInput
        value={value}
        disabled={disabled}
        leftSection={leftSection}
        rightSection={rightSection}
        onFocus={(event) => {
          setFocused(true);
          onFocus?.(event);
        }}
        onBlur={(event) => {
          setFocused(false);
          onBlur?.(event);
        }}
        styles={{
          input: {
            height: inputHeight,
            // Bez fiksnog paddinga na strani koja ima leftSection/rightSection —
            // Mantine sam rezervira taj prostor preko --input-left/right-section-width;
            // fiksni paddingInline na obje strane gura tekst ispod ikone.
            paddingLeft: leftSection ? undefined : 13,
            paddingRight: rightSection ? undefined : 13,
            borderRadius: 6,
            fontSize: 14.5,
            ...colors,
            ...(inputBackground && !disabled && { backgroundColor: inputBackground })
          }
        }}
        {...inputProps}
      />
      {(helperText || error) && (
        <FieldHelper hasError={hasError}>{error ?? helperText}</FieldHelper>
      )}
    </Stack>
  );
};

interface FormPasswordInputProps extends Omit<PasswordInputProps, 'styles' | 'label'> {
  label?: ReactNode;
  required?: boolean;
  helperText?: ReactNode;
  error?: ReactNode;
  inputHeight?: number;
  inputBackground?: string;
}

export const FormPasswordInput = ({
  label,
  required,
  helperText,
  error,
  disabled,
  value,
  onFocus,
  onBlur,
  inputHeight = 42,
  inputBackground,
  ...inputProps
}: FormPasswordInputProps) => {
  const [focused, setFocused] = useState(false);
  const hasError = !!error;
  const hasValue = value !== null && value !== undefined && value !== '';
  const colors = getFieldColors({ focused, disabled, hasError, hasValue });

  return (
    <Stack gap={6} style={{ opacity: disabled ? 0.6 : 1 }}>
      {label && <FieldLabel label={label} required={required} hasError={hasError} />}
      <PasswordInput
        value={value}
        disabled={disabled}
        onFocus={(event) => {
          setFocused(true);
          onFocus?.(event);
        }}
        onBlur={(event) => {
          setFocused(false);
          onBlur?.(event);
        }}
        styles={{
          input: {
            height: inputHeight,
            borderRadius: 6,
            ...colors,
            ...(inputBackground && !disabled && { backgroundColor: inputBackground })
          },
          innerInput: {
            height: '100%',
            paddingLeft: 13,
            fontSize: 14.5,
            color: colors.color
          }
        }}
        {...inputProps}
      />
      {(helperText || error) && (
        <FieldHelper hasError={hasError}>{error ?? helperText}</FieldHelper>
      )}
    </Stack>
  );
};

interface FormDateInputProps extends Omit<
  DatePickerInputProps,
  'styles' | 'label' | 'rightSection'
> {
  label?: ReactNode;
  required?: boolean;
  helperText?: ReactNode;
  error?: ReactNode;
  inputHeight?: number;
  inputBackground?: string;
}

export const FormDateInput = ({
  label,
  required,
  helperText,
  error,
  disabled,
  value,
  onFocus,
  onBlur,
  inputHeight = 42,
  inputBackground,
  ...dateProps
}: FormDateInputProps) => {
  const [focused, setFocused] = useState(false);
  const hasError = !!error;
  const hasValue = value !== null && value !== undefined && value !== '';
  const colors = getFieldColors({ focused, disabled, hasError, hasValue });

  return (
    <Stack gap={6} style={{ opacity: disabled ? 0.6 : 1 }}>
      {label && <FieldLabel label={label} required={required} hasError={hasError} />}
      <DatePickerInput
        value={value}
        disabled={disabled}
        onFocus={(event) => {
          setFocused(true);
          onFocus?.(event);
        }}
        onBlur={(event) => {
          setFocused(false);
          onBlur?.(event);
        }}
        rightSection={<IconFigmaCalendar size={14} color={colors.color} />}
        popoverProps={{ classNames: { dropdown: 'date-picker-dropdown' } }}
        styles={{
          input: {
            height: inputHeight,
            paddingLeft: 13,
            paddingRight: 34,
            borderRadius: 6,
            fontSize: 14.5,
            ...colors,
            ...(inputBackground && !disabled && { backgroundColor: inputBackground })
          }
        }}
        {...dateProps}
      />
      {(helperText || error) && (
        <FieldHelper hasError={hasError}>{error ?? helperText}</FieldHelper>
      )}
    </Stack>
  );
};

interface FormDateRangeInputProps extends Omit<
  DatePickerInputProps<'range'>,
  'styles' | 'label' | 'rightSection' | 'type'
> {
  label?: ReactNode;
  required?: boolean;
  helperText?: ReactNode;
  error?: ReactNode;
  inputHeight?: number;
  inputBackground?: string;
  hideCalendarIcon?: boolean;
}

/** Jedno polje "od – do" (klik otvara kalendar, prvi klik bira "od", drugi "do") —
 * za razliku od FormDateInput (jedan datum), ovo vraća [od, do] par u jednom kliku na polje. */
export const FormDateRangeInput = ({
  label,
  required,
  helperText,
  error,
  disabled,
  value,
  onFocus,
  onBlur,
  inputHeight = 42,
  inputBackground,
  hideCalendarIcon,
  ...dateProps
}: FormDateRangeInputProps) => {
  const [focused, setFocused] = useState(false);
  const hasError = !!error;
  const hasValue = !!value?.[0] || !!value?.[1];
  const colors = getFieldColors({ focused, disabled, hasError, hasValue });

  return (
    <Stack gap={6} style={{ opacity: disabled ? 0.6 : 1 }}>
      {label && <FieldLabel label={label} required={required} hasError={hasError} />}
      <DatePickerInput
        type="range"
        value={value}
        disabled={disabled}
        onFocus={(event) => {
          setFocused(true);
          onFocus?.(event);
        }}
        onBlur={(event) => {
          setFocused(false);
          onBlur?.(event);
        }}
        rightSection={
          hideCalendarIcon ? undefined : <IconFigmaCalendar size={14} color={colors.color} />
        }
        popoverProps={{ classNames: { dropdown: 'date-picker-dropdown' } }}
        styles={{
          input: {
            height: inputHeight,
            paddingLeft: 13,
            paddingRight: 34,
            borderRadius: 6,
            fontSize: 14.5,
            ...colors,
            ...(inputBackground && !disabled && { backgroundColor: inputBackground })
          }
        }}
        {...dateProps}
      />
      {(helperText || error) && (
        <FieldHelper hasError={hasError}>{error ?? helperText}</FieldHelper>
      )}
    </Stack>
  );
};

interface FormWeekPickerInputProps {
  label?: ReactNode;
  required?: boolean;
  helperText?: ReactNode;
  error?: ReactNode;
  disabled?: boolean;
  placeholder?: string;
  clearable?: boolean;
  /** '' ili '1'..'52' */
  week: string;
  /** '' ili npr. '2026' */
  year: string;
  onChange: (week: string, year: string) => void;
  /** Dani prije ovog datuma se ne mogu birati (npr. ponedjeljak tjedna "od" — na polju "do"). */
  minDate?: Date;
  /** Dani poslije ovog datuma se ne mogu birati (npr. nedjelja tjedna "do" — na polju "od"). */
  maxDate?: Date;
  inputHeight?: number;
  inputBackground?: string;
}

/** Prikazuje/bira tjedan+godinu (npr. "23/2026") preko kalendara — polje
 * samo nije upisivo (nema slobodnog teksta), klik otvara kalendar pozicioniran
 * na trenutno odabrani tjedan (ponedjeljak tog tjedna), a klik na bilo koji
 * dan u tom tjednu bira taj tjedan/godinu. */
export const FormWeekPickerInput = ({
  label,
  required,
  helperText,
  error,
  disabled,
  placeholder,
  clearable = true,
  week,
  year,
  onChange,
  minDate,
  maxDate,
  inputHeight = 42,
  inputBackground
}: FormWeekPickerInputProps) => {
  const [opened, setOpened] = useState(false);
  const hasError = !!error;
  const hasValue = !!week && !!year;
  const colors = getFieldColors({ focused: opened, disabled, hasError, hasValue });

  const selectedMonday = hasValue ? (isoWeekToDate(Number(week), Number(year)) ?? null) : null;
  const selectedSunday = selectedMonday
    ? new Date(selectedMonday.getTime() + 6 * 24 * 60 * 60 * 1000)
    : null;
  const [viewDate, setViewDate] = useState<Date>(selectedMonday ?? new Date());

  const openPicker = () => {
    if (disabled) return;
    setViewDate(selectedMonday ?? new Date());
    setOpened(true);
  };

  const handleDayClick = (dateStr: string) => {
    const { week: pickedWeek, year: pickedYear } = dateToIsoWeek(parseDateStringValue(dateStr));
    onChange(String(pickedWeek), String(pickedYear));
    setOpened(false);
  };

  return (
    <Stack gap={6} style={{ opacity: disabled ? 0.6 : 1 }}>
      {label && <FieldLabel label={label} required={required} hasError={hasError} />}
      <Popover
        opened={opened}
        onChange={setOpened}
        position="bottom-start"
        withinPortal
        shadow="md"
      >
        <Popover.Target>
          <UnstyledButton
            type="button"
            disabled={disabled}
            onClick={() => (opened ? setOpened(false) : openPicker())}
            style={{
              height: inputHeight,
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              paddingLeft: 13,
              paddingRight: 13,
              borderRadius: 6,
              fontSize: 14.5,
              cursor: disabled ? 'default' : 'pointer',
              ...colors,
              ...(inputBackground && !disabled && { backgroundColor: inputBackground })
            }}
          >
            <span>
              {hasValue && selectedMonday
                ? `${selectedMonday.toLocaleDateString(currentLocale())} (${week}/${year})`
                : (placeholder ?? '')}
            </span>
            {clearable && hasValue && !disabled ? (
              <CloseButton
                size="sm"
                onClick={(event) => {
                  event.stopPropagation();
                  onChange('', '');
                }}
              />
            ) : (
              <IconFigmaCalendar size={14} color={colors.color} />
            )}
          </UnstyledButton>
        </Popover.Target>
        <Popover.Dropdown p={4}>
          <Calendar
            date={toDateStringValue(viewDate)}
            onDateChange={(value) => setViewDate(parseDateStringValue(value))}
            firstDayOfWeek={1}
            highlightToday
            withCellSpacing={false}
            withWeekNumbers
            minDate={minDate}
            maxDate={maxDate}
            getDayProps={(dateStr) => {
              const onClick = () => handleDayClick(dateStr);
              if (!selectedMonday || !selectedSunday) return { onClick };
              const date = parseDateStringValue(dateStr);
              const inRange = date >= selectedMonday && date <= selectedSunday;
              return {
                onClick,
                selected: inRange,
                inRange,
                firstInRange: date.getTime() === selectedMonday.getTime(),
                lastInRange: date.getTime() === selectedSunday.getTime()
              };
            }}
          />
        </Popover.Dropdown>
      </Popover>
      {(helperText || error) && (
        <FieldHelper hasError={hasError}>{error ?? helperText}</FieldHelper>
      )}
    </Stack>
  );
};

interface FormTextareaProps extends Omit<TextareaProps, 'styles' | 'label'> {
  label?: ReactNode;
  required?: boolean;
  helperText?: ReactNode;
  error?: ReactNode;
  maxLength?: number;
  inputBackground?: string;
}

export const FormTextarea = ({
  label,
  required,
  helperText,
  error,
  disabled,
  value,
  onFocus,
  onBlur,
  minRows = 3,
  maxLength,
  inputBackground,
  ...textareaProps
}: FormTextareaProps) => {
  const [focused, setFocused] = useState(false);
  const hasError = !!error;
  const hasValue = value !== null && value !== undefined && value !== '';
  const colors = getFieldColors({ focused, disabled, hasError, hasValue });
  const currentLength = typeof value === 'string' ? value.length : 0;

  return (
    <Stack gap={6} style={{ opacity: disabled ? 0.6 : 1 }}>
      {label && <FieldLabel label={label} required={required} hasError={hasError} />}
      <Box style={{ position: 'relative' }}>
        <Textarea
          value={value}
          disabled={disabled}
          minRows={minRows}
          onFocus={(event) => {
            setFocused(true);
            onFocus?.(event);
          }}
          onBlur={(event) => {
            setFocused(false);
            onBlur?.(event);
          }}
          styles={{
            input: {
              paddingLeft: 13,
              paddingRight: 13,
              paddingTop: 11,
              paddingBottom: maxLength !== undefined ? 22 : 11,
              borderRadius: 6,
              fontSize: 14.5,
              ...colors,
              ...(inputBackground && !disabled && { backgroundColor: inputBackground })
            }
          }}
          {...textareaProps}
        />
        {maxLength !== undefined && (
          <Text
            fz={12}
            style={{
              position: 'absolute',
              bottom: 8,
              right: 13,
              color: 'var(--color-text-muted)',
              opacity: 0.7,
              pointerEvents: 'none'
            }}
          >
            {currentLength}/{maxLength}
          </Text>
        )}
      </Box>
      {(helperText || error) && (
        <FieldHelper hasError={hasError}>{error ?? helperText}</FieldHelper>
      )}
    </Stack>
  );
};

interface FormNumberInputProps extends Omit<NumberInputProps, 'styles' | 'label'> {
  label?: ReactNode;
  required?: boolean;
  helperText?: ReactNode;
  error?: ReactNode;
  inputHeight?: number;
  inputBackground?: string;
}

export const FormNumberInput = ({
  label,
  required,
  helperText,
  error,
  disabled,
  value,
  onFocus,
  onBlur,
  inputHeight = 42,
  inputBackground,
  ...numberProps
}: FormNumberInputProps) => {
  const [focused, setFocused] = useState(false);
  const hasError = !!error;
  const hasValue = value !== null && value !== undefined && value !== '';
  const colors = getFieldColors({ focused, disabled, hasError, hasValue });

  return (
    <Stack gap={6} style={{ opacity: disabled ? 0.6 : 1 }}>
      {label && <FieldLabel label={label} required={required} hasError={hasError} />}
      <NumberInput
        value={value}
        disabled={disabled}
        hideControls
        onFocus={(event) => {
          setFocused(true);
          onFocus?.(event);
        }}
        onBlur={(event) => {
          setFocused(false);
          onBlur?.(event);
        }}
        styles={{
          input: {
            height: inputHeight,
            paddingLeft: 13,
            paddingRight: 13,
            borderRadius: 6,
            fontSize: 14.5,
            ...colors,
            ...(inputBackground && !disabled && { backgroundColor: inputBackground })
          }
        }}
        {...numberProps}
      />
      {(helperText || error) && (
        <FieldHelper hasError={hasError}>{error ?? helperText}</FieldHelper>
      )}
    </Stack>
  );
};

interface FormCreatableSelectProps {
  label?: ReactNode;
  required?: boolean;
  helperText?: ReactNode;
  error?: ReactNode;
  disabled?: boolean;
  placeholder?: string;
  inputHeight?: number;
  /** Postojeće vrijednosti za odabir (npr. kategorije već korištene na
   * drugim stavkama) — ako upisani tekst ne postoji u ovoj listi, korisnik
   * ga može dodati kao novu vrijednost preko "+ Dodaj ..." opcije. */
  data: string[];
  value?: string;
  onChange: (value: string) => void;
  createLabel?: (query: string) => ReactNode;
  emptyLabel?: ReactNode;
  inputBackground?: string;
}

const CREATE_OPTION_VALUE = '$create';

export const FormCreatableSelect = ({
  label,
  required,
  helperText,
  error,
  disabled,
  placeholder,
  inputHeight = 42,
  inputBackground,
  data,
  value = '',
  onChange,
  createLabel,
  emptyLabel = '—'
}: FormCreatableSelectProps) => {
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption()
  });
  const [search, setSearch] = useState(value ?? '');
  const [focused, setFocused] = useState(false);
  const [sheetOpened, setSheetOpened] = useState(false);
  const isMobile = useMediaQuery('(max-width: 48em)');
  const hasError = !!error;
  const hasValue = !!value;
  const colors = getFieldColors({ focused, disabled, hasError, hasValue });
  const inputStyles = {
    input: {
      height: inputHeight,
      paddingLeft: 13,
      paddingRight: 34,
      borderRadius: 6,
      fontSize: 14.5,
      ...colors,
      ...(inputBackground && !disabled && { backgroundColor: inputBackground })
    }
  };

  useEffect(() => {
    setSearch(value ?? '');
  }, [value]);

  if (isMobile) {
    return (
      <Stack gap={6} style={{ opacity: disabled ? 0.6 : 1 }}>
        {label && <FieldLabel label={label} required={required} hasError={hasError} />}
        <InputBase
          component="input"
          readOnly
          disabled={disabled}
          placeholder={placeholder}
          value={value ?? ''}
          onClick={() => setSheetOpened(true)}
          rightSection={<IconFigmaChevronDown size={14} color={colors.color} />}
          rightSectionPointerEvents="none"
          styles={inputStyles}
        />
        <MobileSelectSheet
          opened={sheetOpened}
          onClose={() => setSheetOpened(false)}
          title={label}
          data={data}
          value={value}
          searchable
          emptyLabel={emptyLabel}
          createLabel={createLabel}
          onCreate={(query) => {
            onChange(query);
            setSheetOpened(false);
          }}
          onSelect={(item) => {
            onChange(item.value);
            setSheetOpened(false);
          }}
        />
        {(helperText || error) && (
          <FieldHelper hasError={hasError}>{error ?? helperText}</FieldHelper>
        )}
      </Stack>
    );
  }

  const trimmedSearch = search.trim();
  const filtered = data.filter((item) => item.toLowerCase().includes(trimmedSearch.toLowerCase()));
  const exactMatch = data.some((item) => item.toLowerCase() === trimmedSearch.toLowerCase());
  const showCreateOption = trimmedSearch.length > 0 && !exactMatch;

  return (
    <Stack gap={6} style={{ opacity: disabled ? 0.6 : 1 }}>
      {label && <FieldLabel label={label} required={required} hasError={hasError} />}
      <Combobox
        store={combobox}
        onOptionSubmit={(optionValue) => {
          const nextValue = optionValue === CREATE_OPTION_VALUE ? trimmedSearch : optionValue;
          onChange(nextValue);
          setSearch(nextValue);
          combobox.closeDropdown();
        }}
      >
        <Combobox.Target>
          <InputBase
            component="input"
            disabled={disabled}
            placeholder={placeholder}
            value={search}
            onChange={(event) => {
              setSearch(event.currentTarget.value);
              combobox.openDropdown();
              combobox.updateSelectedOptionIndex();
            }}
            onClick={() => combobox.openDropdown()}
            onFocus={() => {
              setFocused(true);
              // Prazni search na fokus (umjesto zadržavanja trenutne
              // vrijednosti) da odmah vidimo CIJELU listu kategorija, ne
              // samo one koje slučajno sadrže trenutno upisani tekst.
              setSearch('');
              combobox.openDropdown();
            }}
            onBlur={() => {
              setFocused(false);
              setSearch(value ?? '');
              combobox.closeDropdown();
            }}
            rightSection={<Combobox.Chevron />}
            styles={inputStyles}
          />
        </Combobox.Target>

        <Combobox.Dropdown
          style={{
            backgroundColor: 'var(--color-bg-surface)',
            borderColor: 'var(--color-border-default)'
          }}
        >
          <Combobox.Options>
            {filtered.map((item) => (
              <Combobox.Option value={item} key={item} active={item === value}>
                {item}
              </Combobox.Option>
            ))}
            {showCreateOption && (
              <Combobox.Option value={CREATE_OPTION_VALUE}>
                {createLabel ? createLabel(trimmedSearch) : `+ ${trimmedSearch}`}
              </Combobox.Option>
            )}
            {filtered.length === 0 && !showCreateOption && (
              <Combobox.Empty>{emptyLabel}</Combobox.Empty>
            )}
          </Combobox.Options>
        </Combobox.Dropdown>
      </Combobox>
      {(helperText || error) && (
        <FieldHelper hasError={hasError}>{error ?? helperText}</FieldHelper>
      )}
    </Stack>
  );
};
